(() => {
  const cartSelector = '#main-cart-items';
  const quantitySelector = `${cartSelector} [data-cart-quantity]`;

  const findItemRow = (container, itemKey) => {
    return Array.from(container.querySelectorAll('.cart-item[data-line]')).find(
      (row) => row.dataset.line === itemKey
    );
  };

  const copyElementContent = (currentElement, nextElement) => {
    if (!currentElement || !nextElement) return;

    currentElement.innerHTML = nextElement.innerHTML;

    if (nextElement.hasAttribute('style')) {
      currentElement.setAttribute('style', nextElement.getAttribute('style'));
    } else {
      currentElement.removeAttribute('style');
    }
  };

  const copyMatchedElements = (currentContainer, nextContainer, selector) => {
    const currentElements = currentContainer.querySelectorAll(selector);
    const nextElements = nextContainer.querySelectorAll(selector);

    currentElements.forEach((currentElement, index) => {
      const nextElement = nextElements[index];
      if (!nextElement) return;

      currentElement.innerHTML = nextElement.innerHTML;
      Array.from(currentElement.attributes).forEach((attribute) => {
        if (attribute.name.startsWith('data-')) currentElement.removeAttribute(attribute.name);
      });
      Array.from(nextElement.attributes).forEach((attribute) => {
        if (attribute.name.startsWith('data-')) currentElement.setAttribute(attribute.name, attribute.value);
      });
    });
  };

  const formatMoney = (amount) => {
    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(amount, window.money_format);
    }

    return `$${(amount / 100).toFixed(2)}`;
  };

  const updateCheckoutAvailability = (itemCount) => {
    const checkoutButton = document.getElementById('cart-checkout');
    if (!checkoutButton) return;

    const minimumNotMet = itemCount < 20;
    const termsCheckbox = document.getElementById('cart_conditions');
    const termsNotAccepted = Boolean(termsCheckbox && !termsCheckbox.checked);

    checkoutButton.classList.toggle('button--minimum-order-disabled', minimumNotMet);
    checkoutButton.disabled = minimumNotMet || termsNotAccepted;
  };

  const updateHeaderCount = (itemCount) => {
    document.querySelectorAll('[data-cart-count]').forEach((element) => {
      element.textContent = itemCount;
    });
    document.querySelectorAll('[data-cart-total-quantity]').forEach((element) => {
      element.textContent = `${itemCount} UNITS`;
    });
  };

  const updateFromCartJson = (row, cart, itemKey) => {
    const item = cart.items.find((cartItem) => cartItem.key === itemKey);
    if (!item) return;

    row.dataset.cartItemQuantity = item.quantity;
    row.querySelectorAll('[data-cart-quantity]').forEach((input) => {
      input.value = item.quantity;
    });
    row.querySelectorAll('[data-item-final-price-display]').forEach((element) => {
      element.innerHTML = formatMoney(item.final_price);
    });
    row.querySelectorAll('[data-item-price-with-quantity-display]').forEach((element) => {
      element.innerHTML = formatMoney(item.final_line_price);
    });

    const subtotal = document.querySelector('[data-cart-total] .cart-total-subtotal .cart-total-value .text');
    const grandTotal = document.querySelector('[data-cart-total] .cart-total-grandtotal .cart-total-value .text');
    if (subtotal) subtotal.innerHTML = formatMoney(cart.original_total_price);
    if (grandTotal) grandTotal.innerHTML = formatMoney(cart.total_price);
  };

  const updateFromRenderedSection = (row, cart, itemKey, sectionHtml) => {
    if (!sectionHtml) {
      updateFromCartJson(row, cart, itemKey);
      return;
    }

    const parsedDocument = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const nextCart = parsedDocument.querySelector(cartSelector);
    const currentCart = document.querySelector(cartSelector);
    if (!nextCart || !currentCart) {
      updateFromCartJson(row, cart, itemKey);
      return;
    }

    if (cart.item_count === 0) {
      currentCart.innerHTML = nextCart.innerHTML;
      return;
    }

    const nextRow = findItemRow(nextCart, itemKey);
    if (nextRow) {
      row.dataset.cartItemQuantity = nextRow.dataset.cartItemQuantity;
      row.querySelectorAll('[data-cart-quantity]').forEach((input) => {
        input.value = nextRow.dataset.cartItemQuantity;
      });
      copyMatchedElements(row, nextRow, '.cart-item-block.cart-item-price');
      copyMatchedElements(row, nextRow, '.cart-item-block.cart-item-total');
    } else {
      row.remove();
    }

    ['.cart-total-subtotal', '.cart-total-grandtotal', '.cart-total-savings'].forEach((selector) => {
      copyElementContent(
        currentCart.querySelector(`[data-cart-total] ${selector}`),
        nextCart.querySelector(`[data-cart-total] ${selector}`)
      );
    });

    const currentShippingMessage = currentCart.querySelector('.haloCalculatorShipping');
    const nextShippingMessage = nextCart.querySelector('.haloCalculatorShipping');
    if (currentShippingMessage && nextShippingMessage) {
      currentShippingMessage.replaceWith(nextShippingMessage);
    }
  };

  const showError = (message) => {
    if (typeof window.showWarning === 'function') {
      window.showWarning(message);
    } else {
      window.alert(message);
    }
  };

  const updateCartItem = async (input, quantity) => {
    const row = input.closest('.cart-item[data-line]');
    const cartRoot = document.querySelector(cartSelector);
    if (!row || !cartRoot || row.classList.contains('is-updating')) return;

    const itemKey = input.dataset.line;
    const previousQuantity = Number(row.dataset.cartItemQuantity);
    const requestedQuantity = Math.max(0, Number.parseInt(quantity, 10) || 0);
    const sectionId = cartRoot.dataset.id;

    row.classList.add('is-updating');

    try {
      const response = await fetch(window.routes?.cart_change_url || '/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          id: itemKey,
          quantity: requestedQuantity,
          sections: sectionId ? [sectionId] : undefined,
          sections_url: window.location.pathname
        })
      });
      const cart = await response.json();

      if (!response.ok) {
        throw new Error(cart.description || cart.message || 'Unable to update the cart.');
      }

      updateFromRenderedSection(row, cart, itemKey, cart.sections?.[sectionId]);
      updateHeaderCount(cart.item_count);
      updateCheckoutAvailability(cart.item_count);

      const giftButton = document.getElementById('cart-gift-wrapping');
      if (giftButton && input.dataset.cartQuantityId === giftButton.dataset.giftId && requestedQuantity === 0) {
        giftButton.dataset.isChecked = 'false';
      }

      if (window.sharedFunctions?.checkNeedToConvertCurrency?.()) {
        window.Currency?.convertAll(
          window.shop_currency,
          window.jQuery?.('#currencies .active').attr('data-currency'),
          'span.money',
          'money_format'
        );
      }

      document.dispatchEvent(new CustomEvent('cart-update', { detail: cart }));
    } catch (error) {
      row.querySelectorAll('[data-cart-quantity]').forEach((quantityInput) => {
        quantityInput.value = previousQuantity;
      });
      showError(error.message);
    } finally {
      row.classList.remove('is-updating');
    }
  };

  const handleQuantityChange = (event) => {
    const input = event.target.closest?.(quantitySelector);
    if (!input) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    updateCartItem(input, input.value);
  };

  const handleRemoveClick = (event) => {
    const removeButton = event.target.closest?.(`${cartSelector} [data-cart-remove]`);
    if (!removeButton) return;

    const row = removeButton.closest('.cart-item[data-line]');
    const input = row?.querySelector('[data-cart-quantity]');
    if (!input) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    input.value = 0;
    updateCartItem(input, 0);
  };

  document.addEventListener('change', handleQuantityChange, true);
  document.addEventListener('click', handleRemoveClick, true);

  document.addEventListener('change', (event) => {
    if (!event.target.matches('#cart_conditions')) return;

    window.setTimeout(() => {
      const uniqueInputs = Array.from(document.querySelectorAll(quantitySelector)).filter(
        (input, index, inputs) => inputs.findIndex((item) => item.dataset.line === input.dataset.line) === index
      );
      const itemCount = uniqueInputs.reduce(
        (total, input) => total + (Number.parseInt(input.value, 10) || 0),
        0
      );
      updateCheckoutAvailability(itemCount);
    }, 0);
  });
})();
